import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowBigRight, Cuboid } from 'lucide-react';
import { convertAssetToMindMapData } from './VrUtils';
import {
  RootNode,
  CategoryNode,
  SubCategoryNode,
  PropertyNode,
  TriggerNode,
  ActionNode,
  EventContainerNode,
} from './VrMindMapNodes';
import {
  VR_TRIGGER_OPTIONS,
  ASSET_MODEL_OPTIONS,
  EVENT_MODES,
  POSITION_SAVE_DEBOUNCE,
} from './VrMindMapConstants';
import {
  getAssetType,
  convertToFlowGraph,
  saveNodePositions,
} from './VrMindMapUtils';
import { useVrMindMapApi } from './useVrMindMapApi';
import AssetSidebar from './AssetSidebar';
import EventModal from './EventModal';
import EditPropertyModal from './EditPropertyModal';
import { useParams } from 'react-router-dom';
import SConnectorEdge from './SConnectorEdge';

const nodeTypes = {
  root: RootNode,
  category: CategoryNode,
  subCategory: SubCategoryNode,
  property: PropertyNode,
  trigger: TriggerNode,
  action: ActionNode,
  eventContainer: EventContainerNode,
};

const edgeTypes = {
  sConnector: SConnectorEdge,
};

const VRAssetMindMap = () => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventMode, setEventMode] = useState(EVENT_MODES.ADD);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [pendingEventId, setPendingEventId] = useState(null);
  const [eventForm, setEventForm] = useState({
    vr_trigger: '',
    action: '',
  });
  const { id: projectId } = useParams();
  const [selectedNode, setSelectedNode] = useState(null);
  const [editValue, setEditValue] = useState('');

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Track previous node positions to calculate movement deltas
  const previousPositionsRef = useRef(new Map());

  const baseUrl = 'https://devserver.spatialgrid.ai';

  const {
    assets,
    loading,
    saving,
    fetchAssets,
    fetchAssetDetails,
    updateNodeValue: apiUpdateNodeValue,
    handleTriggerSelection: apiHandleTriggerSelection,
    handleEventSubmit: apiHandleEventSubmit,
    handleEventDelete: apiHandleEventDelete,
  } = useVrMindMapApi(projectId, baseUrl);

  useEffect(() => {
    const loadAssets = async () => {
      const data = await fetchAssets();
      if (data) {
        const firstAsset =
          data.mesh?.[0] ||
          data.video?.[0] ||
          data.audio?.[0] ||
          data.image?.[0] ||
          data.text?.[0];
        if (firstAsset) {
          setSelectedAsset({ ...firstAsset, type: getAssetType(firstAsset) });
        }
      }
    };
    loadAssets();
  }, [fetchAssets]);

  const refreshSelectedAsset = useCallback(
    async (assetOverride = null) => {
      const asset = assetOverride || selectedAsset;
      if (!asset) return null;

      const assetId = asset.id || asset._id;
      if (!assetId) return null;

      const updatedAsset = await fetchAssetDetails(assetId);
      if (updatedAsset) {
        const typedAsset = {
          ...updatedAsset,
          type: getAssetType(updatedAsset),
        };
        setSelectedAsset(typedAsset);
        return typedAsset;
      }

      const assetsData = await fetchAssets();
      if (assetsData) {
        const allAssets = [
          ...(assetsData.mesh || []),
          ...(assetsData.video || []),
          ...(assetsData.audio || []),
          ...(assetsData.image || []),
          ...(assetsData.text || []),
        ];
        const fallbackAsset = allAssets.find(
          (a) => (a.id || a._id) === assetId
        );
        if (fallbackAsset) {
          const typedAsset = {
            ...fallbackAsset,
            type: getAssetType(fallbackAsset),
          };
          setSelectedAsset(typedAsset);
          return typedAsset;
        }
      }

      return null;
    },
    [selectedAsset, fetchAssetDetails, fetchAssets]
  );

  const openAddEventModal = useCallback(() => {
    setEventMode(EVENT_MODES.ADD_TRIGGER);
    setCurrentEvent(null);
    setPendingEventId(null);
    setEventForm({
      vr_trigger: '',
      action: '',
    });
    setShowEventModal(true);
  }, []);

  const openAddActionModal = useCallback((eventId, triggerValue) => {
    setEventMode(EVENT_MODES.ADD_ACTION);
    setCurrentEvent({ _id: eventId });
    setPendingEventId(eventId);
    setEventForm({
      vr_trigger: triggerValue,
      action: '',
    });
    setShowEventModal(true);
  }, []);

  const openEditEventModal = useCallback((event) => {
    setEventMode(EVENT_MODES.EDIT);
    setCurrentEvent(event);
    setEventForm({
      vr_trigger: event.vr_trigger || '',
      action: event.action || '',
    });
    setShowEventModal(true);
  }, []);

  const handleEditNode = useCallback(
    (nodeData) => {
      if (nodeData.originalData) {
        if (nodeData.originalData.type) {
          setSelectedNode({ data: { originalData: nodeData.originalData } });
          setEditValue(nodeData.originalData.value);
        } else if (nodeData.originalData.eventData) {
          openEditEventModal(nodeData.originalData.eventData);
        }
      }
    },
    [openEditEventModal]
  );

  const handleDeleteEvent = useCallback(
    async (event) => {
      if (!selectedAsset) return;

      const success = await apiHandleEventDelete(event, selectedAsset);
      if (success) {
        await refreshSelectedAsset(selectedAsset);
      }
    },
    [selectedAsset, apiHandleEventDelete, refreshSelectedAsset]
  );

  const convertToFlowGraphMemo = useCallback(
    (assetData) => {
      return convertToFlowGraph(
        assetData,
        selectedAsset,
        handleEditNode,
        openAddEventModal,
        openAddActionModal,
        handleDeleteEvent,
        400,
        100
      );
    },
    [
      selectedAsset,
      handleEditNode,
      openAddEventModal,
      openAddActionModal,
      handleDeleteEvent,
    ]
  );

  useEffect(() => {
    if (!selectedAsset) {
      setNodes([]);
      setEdges([]);
      previousPositionsRef.current.clear();
      return;
    }

    const assetData = convertAssetToMindMapData(
      selectedAsset,
      VR_TRIGGER_OPTIONS,
      [],
      ASSET_MODEL_OPTIONS
    );

    const { nodes: flowNodes, edges: flowEdges } =
      convertToFlowGraphMemo(assetData);
    setNodes(flowNodes);
    setEdges(flowEdges);

    // Initialize previous positions
    flowNodes.forEach((node) => {
      previousPositionsRef.current.set(node.id, {
        x: node.position.x,
        y: node.position.y,
      });
    });
  }, [selectedAsset, convertToFlowGraphMemo, setNodes, setEdges]);

  // Save node positions when they change
  useEffect(() => {
    if (!selectedAsset || nodes.length === 0) return;

    const assetId = selectedAsset.id || selectedAsset._id;
    if (!assetId) return;

    const positions = {};
    nodes.forEach((node) => {
      if (node.data.stableKey) {
        positions[node.data.stableKey] = {
          x: node.position.x,
          y: node.position.y,
        };
      }
    });

    const timeoutId = setTimeout(() => {
      saveNodePositions(assetId, positions);
    }, POSITION_SAVE_DEBOUNCE);

    return () => clearTimeout(timeoutId);
  }, [nodes, selectedAsset]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Helper function to find all child nodes recursively
  const getChildNodes = useCallback((nodeId, allEdges) => {
    const children = [];
    const visited = new Set();

    const findChildren = (parentId) => {
      if (visited.has(parentId)) return;
      visited.add(parentId);

      allEdges.forEach((edge) => {
        if (edge.source === parentId && !visited.has(edge.target)) {
          children.push(edge.target);
          findChildren(edge.target); // Recursively find grandchildren
        }
      });
    };

    findChildren(nodeId);
    return children;
  }, []);

  // Custom handler to move child nodes with parent
  const handleNodesChange = useCallback(
    (changes) => {
      // First, apply non-position changes normally
      const positionChanges = [];
      const otherChanges = [];

      for (const change of changes) {
        if (change.type === 'position' && change.position) {
          positionChanges.push(change);
        } else {
          otherChanges.push(change);
        }
      }

      // Handle non-position changes first
      if (otherChanges.length > 0) {
        onNodesChange(otherChanges);
      }

      // Handle position changes with child movement
      if (positionChanges.length > 0) {
        setNodes((currentNodes) => {
          const updatedNodes = [...currentNodes];
          const positionsToUpdate = new Map();

          for (const change of positionChanges) {
            const nodeId = change.id;
            const nodeIndex = updatedNodes.findIndex((n) => n.id === nodeId);

            if (nodeIndex !== -1) {
              const node = updatedNodes[nodeIndex];
              const previousPos = previousPositionsRef.current.get(nodeId) || {
                x: node.position.x,
                y: node.position.y,
              };

              // Calculate the movement delta
              const deltaX = change.position.x - previousPos.x;
              const deltaY = change.position.y - previousPos.y;

              // Only move children if there's actual movement
              if (deltaX !== 0 || deltaY !== 0) {
                // Find all child nodes recursively using current edges from closure
                const childNodeIds = getChildNodes(nodeId, edges);

                // Update parent node position
                updatedNodes[nodeIndex] = {
                  ...node,
                  position: change.position,
                };
                positionsToUpdate.set(nodeId, change.position);

                // Update all child nodes
                for (const childId of childNodeIds) {
                  const childIndex = updatedNodes.findIndex(
                    (n) => n.id === childId
                  );
                  if (childIndex !== -1) {
                    const childNode = updatedNodes[childIndex];
                    const newPosition = {
                      x: childNode.position.x + deltaX,
                      y: childNode.position.y + deltaY,
                    };

                    updatedNodes[childIndex] = {
                      ...childNode,
                      position: newPosition,
                    };
                    positionsToUpdate.set(childId, newPosition);
                  }
                }
              } else {
                // No movement, just update position
                updatedNodes[nodeIndex] = {
                  ...node,
                  position: change.position,
                };
                positionsToUpdate.set(nodeId, change.position);
              }
            }
          }

          // Update previous positions
          for (const [nodeId, position] of positionsToUpdate) {
            previousPositionsRef.current.set(nodeId, position);
          }

          return updatedNodes;
        });
      }
    },
    [edges, getChildNodes, setNodes, onNodesChange]
  );

  const handleNodeClick = useCallback(
    (event, node) => {
      const isTrigger = node.data.label === 'VR Trigger';
      const hasAction = node.data.hasAction !== false;

      if (isTrigger && !hasAction && node.data.onTriggerClick) {
        node.data.onTriggerClick(node.data);
        return;
      }

      // Check if it's an event node (has eventData)
      if (node.data.originalData?.eventData) {
        openEditEventModal(node.data.originalData.eventData);
        return;
      }

      if (node.data.originalData?.type) {
        setSelectedNode({ data: { originalData: node.data.originalData } });
        setEditValue(node.data.originalData.value);
      } else {
        setSelectedNode(node);
      }
    },
    [openEditEventModal]
  );

  const handleUpdateNodeValue = useCallback(async () => {
    if (!selectedNode?.data?.originalData) return;
    const node = selectedNode.data.originalData;
    const success = await apiUpdateNodeValue(node, editValue, selectedAsset);
    if (success) {
      await refreshSelectedAsset(selectedAsset);
      await fetchAssets();
      setSelectedNode(null);
    }
  }, [
    selectedNode,
    editValue,
    selectedAsset,
    apiUpdateNodeValue,
    fetchAssets,
    refreshSelectedAsset,
  ]);

  const handleTriggerSelection = useCallback(async () => {
    if (!eventForm.vr_trigger || !selectedAsset) return;

    const success = await apiHandleTriggerSelection(
      eventForm.vr_trigger,
      selectedAsset
    );
    if (success) {
      setShowEventModal(false);
      await refreshSelectedAsset(selectedAsset);
    }
  }, [
    eventForm.vr_trigger,
    selectedAsset,
    apiHandleTriggerSelection,
    refreshSelectedAsset,
  ]);

  const handleEventSubmit = useCallback(async () => {
    if (!selectedAsset) return;

    const success = await apiHandleEventSubmit(
      eventMode,
      eventForm,
      selectedAsset,
      pendingEventId,
      currentEvent
    );
    if (success) {
      setShowEventModal(false);
      setPendingEventId(null);
      await refreshSelectedAsset(selectedAsset);
    }
  }, [
    eventMode,
    eventForm,
    selectedAsset,
    pendingEventId,
    currentEvent,
    apiHandleEventSubmit,
    refreshSelectedAsset,
  ]);

  const handleEventDeleteFromModal = useCallback(async () => {
    if (!selectedAsset || !currentEvent) return;

    const success = await apiHandleEventDelete(currentEvent, selectedAsset);
    if (success) {
      setShowEventModal(false);
      await refreshSelectedAsset(selectedAsset);
    }
  }, [currentEvent, selectedAsset, apiHandleEventDelete, refreshSelectedAsset]);

  const edgeOptions = {
    type: 'sConnector',
    style: { stroke: '#EE2B2A', strokeWidth: 2 },
    animated: false,
  };

  const defaultEdgeOptions = edgeOptions;

  return (
    <div className="w-full h-screen bg-[#121316] flex overflow-hidden">
      <style>{`
        .react-flow__edge-path {
          stroke: #EE2B2A;
          stroke-width: 2;
        }
        .react-flow__edge.selected .react-flow__edge-path {
          stroke: #EE2B2A;
          stroke-width: 3;
        }
        .react-flow__edge:hover .react-flow__edge-path {
          stroke: #d02524;
        }
        .react-flow__connection-line {
          stroke: #EE2B2A;
        }
        .react-flow__handle {
          background: #EE2B2A;
          border: 2px solid #121316;
        }
        .react-flow__handle:hover {
          background: #d02524;
        }
        .react-flow__controls-button {
          background: #2B2C2F;
          border-bottom: 1px solid #37383F;
          color: #fff;
        }
        .react-flow__controls-button:hover {
          background: #37383F;
        }
        .react-flow__minimap {
          background: #1A1B1E;
        }
        .react-flow__attribution {
          background: rgba(26, 27, 30, 0.8);
          color: #9E9D9D;
        }
        /* Custom dropdown styling to match theme */
        select {
          background-color: #131418 !important;
          color: #ffffff !important;
          border: 1px solid #37383F !important;
        }
        select option {
          background-color: #131418 !important;
          color: #ffffff !important;
        }
        select:focus {
          outline: none;
          border-color: #EE2B2A !important;
          box-shadow: 0 0 0 2px rgba(238, 43, 42, 0.2) !important;
        }
        select:hover {
          border-color: #37383F !important;
        }
      `}</style>
      <AssetSidebar
        assets={assets}
        loading={loading}
        selectedAsset={selectedAsset}
        onAssetSelect={setSelectedAsset}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative bg-[#121316]">
          {selectedAsset ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={handleNodeClick}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              attributionPosition="bottom-left"
            >
              <Background color="#2B2C2F" gap={16} />
              <Controls className="bg-[#1A1B1E] border border-[#37383F] rounded-lg" />
              <MiniMap
                className="bg-[#1A1B1E] border border-[#37383F] rounded-lg"
                nodeColor="#2B2C2F"
                maskColor="rgba(0, 0, 0, 0.6)"
              />
            </ReactFlow>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Cuboid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  No Asset Selected
                </h2>
                <p className="text-gray-400">
                  Select an asset from the sidebar to view its VR properties
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <EditPropertyModal
        node={selectedNode?.data?.originalData}
        editValue={editValue}
        onValueChange={setEditValue}
        onSave={handleUpdateNodeValue}
        onClose={() => setSelectedNode(null)}
        saving={saving}
      />

      <EventModal
        show={showEventModal}
        onClose={() => setShowEventModal(false)}
        eventMode={eventMode}
        eventForm={eventForm}
        onFormChange={setEventForm}
        onTriggerSelection={handleTriggerSelection}
        onEventSubmit={handleEventSubmit}
        onEventDelete={handleEventDeleteFromModal}
        saving={saving}
        currentEvent={currentEvent}
      />
    </div>
  );
};

export default VRAssetMindMap;
